import type { ChangeEvent, FC, FormEvent } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { NetworkError } from '../../../api/errors/NetworkError';
import { postNickName } from '../../../api/fetchers/nickName/fetcher';
import { setPromotionsScreenName } from '../../../api/fetchers/promotionsV2/fetcher';
import { ScreenNameConstants } from '../../../config/CoreConstants';
import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { configActions } from '../../../store/slices/config/slice';
import { nickNameInfoActions } from '../../../store/slices/nickname/slice';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { FormGrid } from '../../formGrid/FormGrid';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';
import { BaseInput } from '../../inputs/baseInput/BaseInput';
import type { NavDrawerContentProps } from '../../nav/types';

import { NickNameEditFooterMessageConnected } from './NickNameFooterMessageConnected';

import styles from './NickNameEdit.module.css';

interface NickNameEditFormProps extends NavDrawerContentProps {
  inPopUp?: boolean;
  onNickNameSave?: () => void;
}

export const NickNameEditFormConnected: FC<NickNameEditFormProps> = ({
  inNavDrawer = false,
  inPopUp = false,
  onNickNameSave,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [validationMessage, setValidationMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
  const [newScreenName, setNewScreenName] = useState<string>('');
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [errMsgDynamicValues, setErrMsgDynamicValues] = useState<Array<string>>([]);

  const baseUrl = useConfigSlice.baseUrl();
  const env = useConfigSlice.env()?.promoService ?? '';
  const jwtToken = useConfigSlice.jwtToken() ?? '';
  const JSESSIONID = useConfigSlice.JSESSIONID() ?? '';
  const screenName = useConfigSlice.playerConfig()?.screenName ?? '';
  const tableId = useConfigSlice.tableId() ?? '';
  const restrictNicknameEdit = useConfigSlice.isRestrictNickNameEdit() && !inPopUp;

  const dispatch = useCoreDispatch();

  useLayoutEffect(() => {
    if (!inPopUp) {
      inputRef?.current?.focus();
    }
  }, [inPopUp, inputRef]);

  useEffect(() => {
    if (!inPopUp) {
      setNewScreenName(screenName);
    }
  }, [inPopUp, screenName]);

  const handleSaveNickName = (): void => {
    if (isLoading || isSuccessful || restrictNicknameEdit) return;

    setIsLoading(true);
    postNickName({
      JSESSIONID: JSESSIONID,
      screenName: newScreenName,
      tableId: tableId,
      url: `${baseUrl}/api/user/screenName`,
    })
      .then((response) => {
        dispatch(
          nickNameInfoActions.setNickNameInfo({
            maxCount: response.maxCount,
            remainingAttempts: response.maxCount - response.count,
            screenName: response.screenName,
            userId: response.userId,
          })
        );
        dispatch(configActions.setScreenName(response.screenName));

        setIsDisabled(false);
        setIsSuccessful(true);

        onNickNameSave?.();

        setPromotionsScreenName({ envPromo: env, jwtToken: jwtToken });
      })
      .catch((error) => {
        if (error instanceof NetworkError && error.payload instanceof ReadableStream) {
          error.payload
            .getReader()
            .read()
            .then(function processText({ done, value }) {
              if (done) {
                return;
              }

              // Convert stream from Uint8Array to string
              const text = new TextDecoder('utf-8').decode(value);
              const errorResponse = JSON.parse(text);
              switch (errorResponse.error) {
                case 1013:
                  setValidationMessage('SETTINGS_CHAR_ACCEPTED_UNICODE');
                  break;
                case 1014:
                  setValidationMessage('SETTINGS_NICKNAME_EXIST');
                  break;
                case 1016:
                  setValidationMessage('SETTINGS_SELECTED_NICKNAME');
                  break;
                default:
                  setValidationMessage('ERROR_2001');
                  break;
              }
            })
            .catch(() => {
              setValidationMessage('ERROR_2001');
            });
        } else {
          setValidationMessage('ERROR_2001');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const validateInput = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();

    setIsDisabled(false);

    const input = inputRef.current as HTMLInputElement;
    const value = input.value;

    setNewScreenName(value);
    setErrMsgDynamicValues([]);

    switch (true) {
      case value.length < 2:
        setErrMsgDynamicValues([ScreenNameConstants.SCREEN_NAME_MIN_LENGTH]);
        setValidationMessage('SETTINGS_NICKNAME_CHAR');
        break;
      case value.length > 20:
        setErrMsgDynamicValues([ScreenNameConstants.SCREEN_NAME_MAX_LENGTH]);
        setValidationMessage('SETTINGS_NICKNAME_CHAR_MAX');
        break;
      case value === screenName:
        setValidationMessage('SETTINGS_SELECTED_NICKNAME');
        break;
      case input.validity.patternMismatch:
        setValidationMessage('SETTINGS_CHAR_ACCEPTED_UNICODE');
        break;
      default:
        setValidationMessage('');
        break;
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    handleSaveNickName();
  };

  const mods = useModifiers(styles, 'nickNameSettingsForm', { inPopUp }, { withBaseClass: true });

  return (
    <>
      <form className={mods} onSubmit={handleFormSubmit} autoComplete="off">
        <FormGrid>
          <BaseInput
            disabled={isLoading || isSuccessful}
            errorMessage={
              validationMessage ? <L10nText tk={validationMessage} dynamicValues={errMsgDynamicValues} /> : null
            }
            inputRef={inputRef}
            isErrorAnimated
            label={inPopUp ? null : <L10nText tk="SETTINGS_NICKNAME" />}
            minLength={2}
            name="screenName"
            onChange={validateInput}
            pattern="^[\p{L}\p{N}\p{M}]*$" // accepts Only numbers and unicode letters
            required
            supportingText={<L10nText tk="SETTINGS_NICKNAME_LENGTH" />}
            type="text"
            value={newScreenName}
          />
          <Button
            disabled={!!validationMessage || !newScreenName || isDisabled || restrictNicknameEdit}
            loading={isLoading}
            tk={isSuccessful ? '' : inPopUp ? 'STARTPLAYING' : 'SAVE'}
            type="submit"
            variant={ButtonVariant.ACCENT}
          >
            {isSuccessful ? <Icon icon={Icons.CheckMark} /> : null}
          </Button>
        </FormGrid>
      </form>
      {inNavDrawer ? <NickNameEditFooterMessageConnected /> : null}
    </>
  );
};
